import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/Auth";
import ExportData from "../../../components/common/ExportData";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  FileText,
  Calendar,
  Building2,
  RefreshCw,
  TrendingUp,
  Package,
  User,
  ChevronDown,
} from "lucide-react";

const ReconciliationReport = () => {
  const { requestWithAuth } = useAuth();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRange, setDateRange] = useState("Last 30 Days");
  const [reconciliations, setReconciliations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("Consolidated");
  const [branchName, setBranchName] = useState("Consolidated");
  const [tableData, setTableData] = useState([]);

  // Drug Search State
  const [drugs, setDrugs] = useState([]);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDrugDropdown, setShowDrugDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loadingDrugs, setLoadingDrugs] = useState(false);

  // Refs
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  const getDateRange = (value) => {
    let start_date = "";
    let end_date = "";

    if (value === "Today") {
      start_date = formatDate(new Date());
      end_date = formatDate(new Date());
    } else if (value === "Yesterday") {
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 1))
      );
      end_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 1))
      );
    } else if (value === "Last 7 Days") {
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 7))
      );
      end_date = formatDate(new Date());
    } else if (value === "Last 30 Days") {
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 30))
      );
      end_date = formatDate(new Date());
    } else if (value === "This Month") {
      start_date = formatDate(new Date(new Date().setDate(1)));
      end_date = formatDate(new Date());
    } else if (value === "Last Month") {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      start_date = formatDate(
        new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1)
      );
      end_date = formatDate(
        new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0)
      );
    }

    setStartDate(start_date);
    setEndDate(end_date);
    setDateRange(value);
  };

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?paginated=false`,
        "GET"
      );
      setBranches(data);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const getDrugs = async (search_query) => {
    setLoadingDrugs(true);
    setHighlightedIndex(-1);
    try {
      const data = await requestWithAuth(
        `/inventory/company/?page=1&pageSize=100&search=${search_query}`,
        "GET"
      );
      setDrugs(data?.results || []);
    } catch (error) {
      console.error("Error fetching drugs:", error);
      setDrugs([]);
    } finally {
      setLoadingDrugs(false);
    }
  };

  const handleSelectDrug = (drugItem) => {
    setSelectedDrug(drugItem);
    setSearch("");
    setShowDrugDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDrugDropdown) {
      if (e.key === "Enter" || e.key === "ArrowDown") {
        setShowDrugDropdown(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < drugs.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < drugs.length) {
        handleSelectDrug(drugs[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowDrugDropdown(false);
    }
  };

  const getReconciliations = async (pageSize, currentPage) => {
    try {
      setLoading(true);
      const drugFilter = selectedDrug ? `&search=${selectedDrug?.name}` : "";
      const data = await requestWithAuth(
        `/report/reconciliation/?branch=${branch}&startDate=${startDate}&endDate=${endDate}${drugFilter}&page=${currentPage}&pageSize=${pageSize}`,
        "GET"
      );
      setReconciliations(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      setReconciliations(null);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async () => {
    try {
      const drugFilter = selectedDrug ? `&search=${selectedDrug?.name}` : "";
      const data = await requestWithAuth(
        `/report/reconciliation/?branch=${branch}&startDate=${startDate}&endDate=${endDate}${drugFilter}&paginated=false`,
        "GET"
      );
      setTableData(data?.results);
    } catch (error) {
      setTableData([]);
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    getReconciliations(pageSize, 1);
    getTableData();
  };

  const clearFilters = () => {
    setBranch("Consolidated");
    setBranchName("Consolidated");
    setSelectedDrug(null);
    setSearch("");
    setDrugs([]);
    getDateRange("Last 30 Days");
    setCurrentPage(1);
  };

  // Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch Drugs on Debounce
  useEffect(() => {
    if (debouncedSearch.length >= 2 && branch && branch !== "Consolidated") {
      getDrugs(debouncedSearch);
    } else {
      setDrugs([]);
    }
  }, [debouncedSearch, branch]);

  // Auto-focus input when dropdown opens
  useEffect(() => {
    if (showDrugDropdown && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showDrugDropdown]);

  // Handle Outside Click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDrugDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const selectedElement = listRef.current.children[highlightedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [highlightedIndex]);

  useEffect(() => {
    fetchBranches();
    getDateRange("Last 30 Days");
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      getReconciliations(pageSize, currentPage);
      getTableData();
    }
  }, [currentPage, pageSize]);

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const calculateQuantityChange = (previous, newQty) => {
    const change = newQty - previous;
    return {
      value: Math.abs(change),
      isIncrease: change > 0,
      isDecrease: change < 0,
    };
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Reports</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Reconciliation Report
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <RefreshCw size={32} className="text-blue-600" />
                Inventory Reconciliation Report
              </h1>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <RefreshCw className="text-green-600" size={20} />
              </div>
              <h3 className="text-sm font-medium text-gray-600">
                Total Reconciliations
              </h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {reconciliations?.length || 0}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="text-blue-600" size={20} />
              </div>
              <h3 className="text-sm font-medium text-gray-600">
                Items Reconciled
              </h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {reconciliations?.length || 0}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 className="text-purple-600" size={20} />
              </div>
              <h3 className="text-sm font-medium text-gray-600">Branch</h3>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {branchName === "Consolidated" ? "All Branches" : branchName}
            </p>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Section */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Filter Reconciliation Report
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Branch Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Building2 size={12} className="inline mr-1" />
                  Branch
                </label>
                <select
                  value={branch}
                  onChange={(e) => {
                    if (e.target.value === "Consolidated") {
                      setBranch("Consolidated");
                      setBranchName("Consolidated");
                    } else {
                      const id = Number(e.target.value);
                      const selectedBranch = branches.find((b) => b.id === id);
                      setBranch(id);
                      setBranchName(selectedBranch?.name || "");
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="Consolidated">All Branches</option>
                  {branches?.map((branchItem, index) => (
                    <React.Fragment key={index}>
                      {!branchItem?.is_head_office && (
                        <option value={branchItem.id}>{branchItem.name}</option>
                      )}
                    </React.Fragment>
                  ))}
                </select>
              </div>

              {/* Date Range Preset */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Calendar size={12} className="inline mr-1" />
                  Date Range
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => getDateRange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="Today">Today</option>
                  <option value="Yesterday">Yesterday</option>
                  <option value="Last 7 Days">Last 7 Days</option>
                  <option value="Last 30 Days">Last 30 Days</option>
                  <option value="This Month">This Month</option>
                  <option value="Last Month">Last Month</option>
                  <option value="Custom">Custom Range</option>
                </select>
              </div>

              {/* Drug Selection with Enhanced Search */}
              <div className="relative" ref={dropdownRef}>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Search size={12} className="inline mr-1" />
                  Search Drug
                </label>

                <div
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white cursor-pointer flex items-center justify-between hover:border-blue-500 transition-colors"
                  onClick={() => setShowDrugDropdown(!showDrugDropdown)}
                >
                  <span className={selectedDrug ? "text-gray-900" : "text-gray-400"}>
                    {selectedDrug?.name || "Search and select a drug..."}
                  </span>
                  <ChevronDown size={16} className="text-gray-400" />
                </div>

                {showDrugDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={"Type at least 2 characters..."}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500 disabled:bg-gray-100"
                      />
                    </div>

                    <div className="max-h-60 overflow-y-auto" ref={listRef}>
                      {loadingDrugs ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          <div className="animate-spin inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2"></div>
                          Searching...
                        </div>
                      ) : drugs.length > 0 ? (
                        drugs.map((item, index) => (
                          <div
                            key={index}
                            onClick={() => handleSelectDrug(item)}
                            className={`px-4 py-2 text-sm cursor-pointer transition-colors ${index === highlightedIndex
                              ? "bg-blue-50 text-blue-700"
                              : "text-gray-700 hover:bg-gray-50"
                              }`}
                          >
                            <div className="font-medium">{item?.name}</div>
                            <div className="text-xs text-gray-500">
                              GHS {item?.cash_price}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {branch === "Consolidated"
                            ? "Select a specific branch first"
                            : search.length < 2
                              ? "Type to search..."
                              : "No drugs found"}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Custom Date Range */}
            {dateRange === "Custom" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                </div>
              </div>
            )}

            {/* Filter Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>

          {/* Controls Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData
                  tableData={tableData}
                  fileName="Reconciliation Report"
                />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={(e) => setPageSize(e.target.value)}
                  value={pageSize}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <div className="text-sm text-gray-600">
                Period: <span className="font-semibold">{startDate}</span> to{" "}
                <span className="font-semibold">{endDate}</span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading reconciliation data...</span>
                </div>
              </div>
            ) : reconciliations?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Drug Name
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Previous Qty
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      New Qty
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Change
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Reconciled By
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reconciliations?.map((item, index) => {
                    const change = calculateQuantityChange(
                      item?.previous_quantity,
                      item?.new_quantity
                    );
                    return (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-6 text-sm font-medium text-gray-900">
                          {item?.inventory}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {item?.branch}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {item?.previous_quantity}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {item?.new_quantity}
                        </td>
                        <td className="py-4 px-6 text-sm">
                          {change.isIncrease ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-100 text-green-700 font-medium">
                              <TrendingUp size={14} />+{change.value}
                            </span>
                          ) : change.isDecrease ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-100 text-red-700 font-medium">
                              <TrendingUp size={14} className="rotate-180" />-
                              {change.value}
                            </span>
                          ) : (
                            <span className="text-gray-400">No change</span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <User size={14} className="text-gray-400" />
                            {item?.reconciled_by}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {item?.created_at}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <RefreshCw className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <p className="text-gray-500 text-sm">
                  No reconciliation data found
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Try adjusting your filters
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {reconciliations?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReconciliationReport;
